import { Injectable, OnDestroy } from "@angular/core";
import { Subject, Subscription, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class PhiTimerService implements OnDestroy {
  // private readonly TIMER_DURATION_MS = 15 * 1000; // 15 seconds in milliseconds
  private readonly TIMER_DURATION_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
  private readonly STORAGE_KEY = "phi_timer_expiry";

  private timerSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();
  private timerExpired$ = new Subject<void>();


  private expiryTime: number | null = null;


  private isExpiring = false; // Guard flag to prevent duplicate emissions
  private isNotificationShowing = false; // Guard flag to prevent duplicate notification dialogs

  constructor() {
    this.initializeFromStorage();

  }

  get onTimerExpired$() {
    return this.timerExpired$.asObservable();
  }

  // Check if notification dialog is currently being shown
  isNotificationDialogShowing(): boolean {
    return this.isNotificationShowing;
  }

  // Mark that notification dialog is being shown
  setNotificationShowing(showing: boolean): void {
    this.isNotificationShowing = showing;
  }

  // Starts the 15-minute PHI access timer
  startTimer(): void {
    // Stop any existing timer first
    this.stopTimer();

    // Calculate expiry time
    this.expiryTime = Date.now() + this.TIMER_DURATION_MS;

    // Store expiry time in sessionStorage (clears on page refresh)
    sessionStorage.setItem(this.STORAGE_KEY, this.expiryTime.toString());

    // Start the timer
    this.timerSubscription = timer(this.TIMER_DURATION_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleTimerExpiry();
      });
  }

  // Stops the PHI timer and clears stored state
  stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }

    this.expiryTime = null;

    this.isNotificationShowing = false; // Reset notification flag
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  // Checks if the PHI timer is currently active
  isTimerActive(): boolean {
    if (!this.expiryTime) {
      return false;
    }

    // Check if timer has already expired
    if (Date.now() >= this.expiryTime) {
      this.handleTimerExpiry();
      return false;
    }

    return true;
  }

  // Gets the remaining time in milliseconds
  getRemainingTime(): number {
    if (!this.expiryTime) {
      return 0;
    }



    const remaining = this.expiryTime - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  // Initialize timer from sessionStorage if it exists
  // This handles page navigation within the same session
  private initializeFromStorage(): void {
    const storedExpiry = sessionStorage.getItem(this.STORAGE_KEY);

    if (storedExpiry) {
      const expiryTime = parseInt(storedExpiry, 10);
      const now = Date.now();

      if (expiryTime > now) {
        // Timer is still valid, restart it with remaining time
        const remainingTime = expiryTime - now;
        this.expiryTime = expiryTime;

        this.timerSubscription = timer(remainingTime)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.handleTimerExpiry();
          });
      } else {
        // Timer has already expired
        this.handleTimerExpiry();
      }
    }
  }



  // Handle timer expiry - emit event and clean up
  private handleTimerExpiry(): void {
    // Prevent duplicate emissions
    if (this.isExpiring) {
      return;
    }

    this.isExpiring = true;
    // Clean up timer state
    this.stopTimer();

    // Emit expiry event to all subscribers
    this.timerExpired$.next();

    // Reset flag after emission
    this.isExpiring = false;
  }

  // Cleanup on service destruction
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timerExpired$.complete();
    this.stopTimer();
  }
}
